import {
  Alert,
  Badge,
  Box,
  Card,
  Group,
  NativeSelect,
  NumberInput,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BETRIEB_STUFEN,
  type BetriebLauf,
  type BetriebQuelle,
  type BetriebStufe,
  type BetriebWarnung,
} from "@msc/shared";

import { useBetriebLogs, useBetriebStatus, type BetriebLogsFilter } from "../../hooks/useBetrieb.js";
import { formatBerlinDateTime } from "../../lib/date.js";

function stufenFarbe(stufe: number): string {
  if (stufe >= 50) return "red";
  if (stufe >= 40) return "orange";
  if (stufe >= 30) return "blue";
  return "gray";
}

function stufenName(stufe: number): string {
  if (stufe >= 60) return "fatal";
  if (stufe >= 50) return "error";
  if (stufe >= 40) return "warn";
  if (stufe >= 30) return "info";
  return "debug";
}

function wert(v: unknown): string {
  if (v === null || v === undefined) return "–";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

function LaufBlock({ titel, lauf }: { titel: string; lauf: BetriebLauf | null }) {
  const { t } = useTranslation();
  const stats = lauf?.stats ? Object.entries(lauf.stats) : [];
  return (
    <Stack gap={4}>
      <Group gap="xs">
        <Text fw={600} size="sm">
          {titel}
        </Text>
        {lauf && (
          <Badge
            size="sm"
            variant="light"
            color={lauf.status === "success" ? "green" : lauf.status === "failed" ? "red" : "blue"}
          >
            {t(`pages.settings.betrieb.status.${lauf.status}`, { defaultValue: lauf.status })}
          </Badge>
        )}
      </Group>
      {!lauf ? (
        <Text size="sm" c="dimmed">
          {t("pages.settings.betrieb.niemals")}
        </Text>
      ) : (
        <>
          <Text size="xs" c="dimmed">
            {t("pages.settings.betrieb.start")}: {formatBerlinDateTime(lauf.start)} ·{" "}
            {t("pages.settings.betrieb.ende")}:{" "}
            {lauf.ende ? formatBerlinDateTime(lauf.ende) : t("pages.settings.betrieb.laeuft")}
          </Text>
          {stats.length > 0 && (
            <Box component="dl" m={0} style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12 }}>
              {stats.map(([k, v]) => (
                <Box key={k} style={{ display: "contents" }}>
                  <Text component="dt" size="xs" c="dimmed">
                    {k}
                  </Text>
                  <Text component="dd" size="xs" m={0}>
                    {wert(v)}
                  </Text>
                </Box>
              ))}
            </Box>
          )}
          {/* Für Nicht-Admins liefert der Server nur die erste Zeile (gekürzt);
              den vollen Text samt Stack sieht nur `admin`. */}
          {lauf.fehler && (
            <details>
              <summary>{t("pages.settings.betrieb.fehler")}</summary>
              <Text component="pre" size="xs" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {lauf.fehler}
              </Text>
            </details>
          )}
        </>
      )}
    </Stack>
  );
}

function QuellenKarte({ q, warnungen }: { q: BetriebQuelle; warnungen: BetriebWarnung[] }) {
  const { t } = useTranslation();
  const ampel = warnungen.length > 0 ? "red" : "green";
  return (
    <Card withBorder data-quelle={q.quelle} data-ampel={ampel}>
      <Stack gap="xs">
        <Group gap="xs">
          <Badge color={ampel} circle size="xs" aria-hidden />
          <Title order={5}>{q.quelle}</Title>
        </Group>
        {warnungen.map((w) => (
          <Text key={w.code} size="sm" c="red">
            {w.text}
          </Text>
        ))}
        <LaufBlock titel={t("pages.settings.betrieb.letzterLauf")} lauf={q.letzterLauf} />
        <LaufBlock titel={t("pages.settings.betrieb.letzterErfolg")} lauf={q.letzterErfolg} />
      </Stack>
    </Card>
  );
}

function LogsTeil() {
  const { t } = useTranslation();
  const [stufe, setStufe] = useState<BetriebStufe | "">("");
  const [dienst, setDienst] = useState("");
  const [job, setJob] = useState("");
  const [suche, setSuche] = useState("");
  const [seit, setSeit] = useState("");
  const [bis, setBis] = useState("");
  const [limit, setLimit] = useState<number>(200);

  // datetime-local liefert lokale Zeit ohne Zone; die API will einen Zeitpunkt.
  const iso = (v: string) => {
    const d = v ? new Date(v) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toISOString() : undefined;
  };
  const filter: BetriebLogsFilter = {
    stufe: stufe || undefined,
    dienst: dienst.trim() || undefined,
    job: job.trim() || undefined,
    suche: suche.trim() || undefined,
    seit: iso(seit),
    bis: iso(bis),
    limit,
  };
  const { data, isLoading, error } = useBetriebLogs(filter, true);

  return (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <Title order={4}>{t("pages.settings.betrieb.logsTitle")}</Title>
        <Text size="sm" c="dimmed">
          {t("pages.settings.betrieb.logsHint")}
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
          <NativeSelect
            label={t("pages.settings.betrieb.filter.stufe")}
            value={stufe}
            onChange={(e) => setStufe(e.currentTarget.value as BetriebStufe | "")}
            data={[
              { value: "", label: "–" },
              ...BETRIEB_STUFEN.map((s) => ({ value: s, label: t(`pages.settings.betrieb.stufen.${s}`) })),
            ]}
          />
          <TextInput
            label={t("pages.settings.betrieb.filter.dienst")}
            value={dienst}
            onChange={(e) => setDienst(e.currentTarget.value)}
          />
          <TextInput
            label={t("pages.settings.betrieb.filter.job")}
            value={job}
            onChange={(e) => setJob(e.currentTarget.value)}
          />
          <TextInput
            label={t("pages.settings.betrieb.filter.suche")}
            value={suche}
            maxLength={200}
            onChange={(e) => setSuche(e.currentTarget.value)}
          />
          <TextInput
            type="datetime-local"
            label={t("pages.settings.betrieb.filter.seit")}
            value={seit}
            onChange={(e) => setSeit(e.currentTarget.value)}
          />
          <TextInput
            type="datetime-local"
            label={t("pages.settings.betrieb.filter.bis")}
            value={bis}
            onChange={(e) => setBis(e.currentTarget.value)}
          />
          <NumberInput
            label={t("pages.settings.betrieb.filter.limit")}
            min={1}
            max={1000}
            value={limit}
            onChange={(v) => setLimit(typeof v === "number" ? v : 200)}
          />
        </SimpleGrid>
        {error && <Alert color="red">{t("pages.settings.betrieb.logsLadefehler")}</Alert>}
        {isLoading ? (
          <Skeleton h={120} />
        ) : data && data.eintraege.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t("pages.settings.betrieb.keineLogs")}
          </Text>
        ) : (
          <Box style={{ overflowX: "auto" }}>
            <Table striped withTableBorder fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("pages.settings.betrieb.spalten.zeit")}</Table.Th>
                  <Table.Th>{t("pages.settings.betrieb.spalten.dienst")}</Table.Th>
                  <Table.Th>{t("pages.settings.betrieb.spalten.stufe")}</Table.Th>
                  <Table.Th>{t("pages.settings.betrieb.spalten.job")}</Table.Th>
                  <Table.Th>{t("pages.settings.betrieb.spalten.nachricht")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data?.eintraege.map((e, i) => (
                  <Table.Tr key={`${e.zeit}-${i}`}>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>{formatBerlinDateTime(e.zeit)}</Table.Td>
                    <Table.Td>{e.dienst}</Table.Td>
                    <Table.Td>
                      <Badge size="xs" variant="light" color={stufenFarbe(e.stufe)}>
                        {stufenName(e.stufe)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{e.job ?? "–"}</Table.Td>
                    <Table.Td>
                      <div>{e.nachricht}</div>
                      {e.daten !== null && e.daten !== undefined && (
                        <details>
                          <summary>{t("pages.settings.betrieb.daten")}</summary>
                          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
                            {JSON.stringify(e.daten, null, 2)}
                          </pre>
                        </details>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

/** Betrieb-Reiter: Status für alle, Logs nur mit `admin`. */
export function BetriebPanel({ admin }: { admin: boolean }) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useBetriebStatus();

  if (isLoading) return <Skeleton h={160} aria-label={t("pages.settings.betrieb.laden")} />;
  if (error || !data) return <Alert color="red">{t("pages.settings.betrieb.ladefehler")}</Alert>;

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
        {t("pages.settings.betrieb.hint")}
      </Text>
      <Paper p="md" withBorder>
        <Group gap="xl">
          <Stack gap={0}>
            <Text size="xs" c="dimmed">
              {t("pages.settings.betrieb.version")}
            </Text>
            <Text fw={600}>{data.version}</Text>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed">
              {t("pages.settings.betrieb.umgebung")}
            </Text>
            <Text fw={600}>{data.umgebung || t("pages.settings.betrieb.keineUmgebung")}</Text>
          </Stack>
          <Stack gap={0}>
            <Text size="xs" c="dimmed">
              {t("pages.settings.betrieb.datenbank")}
            </Text>
            <Badge color={data.datenbank === "ok" ? "green" : "red"} variant="light">
              {data.datenbank === "ok"
                ? t("pages.settings.betrieb.datenbankOk")
                : t("pages.settings.betrieb.datenbankFehler")}
            </Badge>
          </Stack>
        </Group>
      </Paper>

      <Stack gap="xs">
        <Title order={4}>{t("pages.settings.betrieb.warnungenTitle")}</Title>
        {data.warnungen.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("pages.settings.betrieb.keineWarnungen")}
          </Text>
        ) : (
          data.warnungen.map((w, i) => (
            <Alert key={`${w.code}-${w.quelle ?? ""}-${i}`} color="orange" variant="light">
              {w.text}
            </Alert>
          ))
        )}
      </Stack>

      <Stack gap="xs">
        <Title order={4}>{t("pages.settings.betrieb.quellenTitle")}</Title>
        {data.quellen.length === 0 ? (
          <Text size="sm" c="dimmed">
            {t("pages.settings.betrieb.keineQuellen")}
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
            {data.quellen.map((q) => (
              <QuellenKarte
                key={q.quelle}
                q={q}
                warnungen={data.warnungen.filter((w) => w.quelle === q.quelle)}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {admin && <LogsTeil />}
    </Stack>
  );
}
